import type { ValidationErrors } from '../../../utils/validation';
import type { ISubsequentService } from '../../interfaces/subsequentService';
import type { IAutoSubsequentService, IService } from './ServiceConsumerAddEdit';
import { PractitionerRoleInRequest } from '../../../../common/enums/practitionerRoleInRequest';
import { ServiceType } from '../../../../common/enums/serviceType';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { Dropdown, FormGroup, FormHeader, FormLabel, InputCheckbox, InputGroup } from '../../elements/common';
import { Component } from '../../elements/Component';
import { ServiceOrganizationProviderSelection } from '../../elements/ServiceOrganizationProviderSelection';

interface IServiceConsumerAutoSubsequentServicesProps {
    autoSubsequentService: IAutoSubsequentService;
    employmentId: string;
    errors: ValidationErrors;
    onChange: (name: keyof IAutoSubsequentService, value: any) => void;
    onDoValidate: (name: keyof IAutoSubsequentService) => void;
    service: IService;
    serviceConsumerId: string | null;
}

interface IServiceConsumerAutoSubsequentServicesState {
    subsequentServices: ISubsequentService[];
    succeedingServiceId: string | null;
    succeedingServiceType?: string;
}

export class ServiceConsumerAutoSubsequentServices extends Component<
    IServiceConsumerAutoSubsequentServicesProps,
    IServiceConsumerAutoSubsequentServicesState
> {
    constructor(props: IServiceConsumerAutoSubsequentServicesProps, context?: {}) {
        super(props, context);

        this.state = {
            subsequentServices: [],
            succeedingServiceId: null,
        };
    }

    public async componentDidMount() {
        await this.getSubsequentServices(this.props.service.id, this.props.employmentId);
        if (this.props.autoSubsequentService.subsequentServiceId) {
            await this.setSucceedingServiceId(this.props.autoSubsequentService.subsequentServiceId);
        }
    }

    public render() {
        return (
            <>
                <FormHeader>{translateKey('subsequentService.autoSubsequentServices')}</FormHeader>

                <FormGroup hasErrors={this.props.errors.has('autoSubsequentService.subsequentServiceId')}>
                    <FormLabel>{translateKey('employment.employment')}</FormLabel>
                    <InputGroup>
                        <Dropdown
                            error={this.props.errors.get('autoSubsequentService.subsequentServiceId')}
                            onChange={async (value) => await this.handleChange('subsequentServiceId', value)}
                            onDoValidate={async () => await this.props.onDoValidate('subsequentServiceId')}
                            placeholder={translateKey(
                                'common.selectItem',
                                translateKey('subsequentService.subsequentService')
                            )}
                            selectOptions={this.state.subsequentServices.map((item) => {
                                return {
                                    value: item.id,
                                    text: item.succeedingService ? item.succeedingService.name : '',
                                };
                            })}
                            value={this.props.autoSubsequentService.subsequentServiceId}
                        />
                    </InputGroup>
                </FormGroup>

                {this.props.autoSubsequentService.subsequentServiceId &&
                    this.state.succeedingServiceId &&
                    this.state.subsequentServices.length > 0 && (
                        <>
                            {this.props.service.serviceType === ServiceType.examination && (
                                <FormGroup hasErrors={this.props.errors.has('autoSubsequentService.isDeviation')}>
                                    <FormLabel>{translateKey('subsequentService.isDeviation')}</FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={this.props.autoSubsequentService.isDeviation}
                                            error={this.props.errors.get('autoSubsequentService.isDeviation')}
                                            onChange={async (value) => await this.props.onChange('isDeviation', value)}
                                            onDoValidate={async () => await this.props.onDoValidate('isDeviation')}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            )}

                            <FormGroup
                                hasErrors={
                                    this.props.errors.has('autoSubsequentService.providerPractitionerId') ||
                                    this.props.errors.has('autoSubsequentService.providerOrganizationId')
                                }
                            >
                                <FormLabel isRequired>
                                    {translateKey('subsequentService.providerOrganization')}
                                </FormLabel>
                                <ServiceOrganizationProviderSelection
                                    error={
                                        this.props.errors.get('autoSubsequentService.providerPractitionerId') ||
                                        this.props.errors.get('autoSubsequentService.providerOrganizationId')
                                    }
                                    initialOrganizationId={this.props.autoSubsequentService.providerOrganizationId}
                                    initialPractitionerId={this.props.autoSubsequentService.providerPractitionerId}
                                    onChange={async (organizationId, practitionerId) => {
                                        await this.props.onChange('providerOrganizationId', organizationId);
                                        await this.props.onChange('providerPractitionerId', practitionerId);
                                    }}
                                    onDoValidate={async () => {
                                        await this.props.onDoValidate('providerOrganizationId');
                                        await this.props.onDoValidate('providerPractitionerId');
                                    }}
                                    roleOfPractitionersInList={
                                        this.state.succeedingServiceType === ServiceType.examination
                                            ? PractitionerRoleInRequest.biometrician
                                            : PractitionerRoleInRequest.specialist
                                    }
                                    serviceProviderServiceId={this.state.succeedingServiceId}
                                />
                            </FormGroup>
                        </>
                    )}
            </>
        );
    }

    private async handleChange<K extends keyof IAutoSubsequentService>(name: K, value: IAutoSubsequentService[K]) {
        await this.props.onChange(name, value);

        if (name === 'subsequentServiceId') {
            if (value && this.state.subsequentServices.length > 0) {
                const succeedingServiceId = this.state.subsequentServices.filter((item) => item.id === value)[0]
                    .succeedingServiceId;
                if (succeedingServiceId) {
                    const succeedingServiceType = await this.getServiceType(succeedingServiceId);
                    await this.setState({
                        succeedingServiceId: succeedingServiceId,
                        succeedingServiceType: succeedingServiceType,
                    });
                }
            }
        }
    }

    private async getServiceType(serviceId: string) {
        const result = await fetchJson(`/api/services/${serviceId}`, 'GET');

        return result.json.serviceType;
    }

    private async setSucceedingServiceId(subsequentServiceId: string) {
        const succeedingService = this.state.subsequentServices.filter((item) => item.id === subsequentServiceId)[0];
        if (succeedingService && succeedingService.succeedingService) {
            const succeedingServiceId = succeedingService!.succeedingService!.id;
            await this.setState({ succeedingServiceId: succeedingServiceId });
        }
    }

    private async getSubsequentServices(serviceId: string, employmentId: string) {
        const url = `/api/subsequentServices?serviceId=${serviceId}&employmentId=${employmentId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ subsequentServices: result.json.items });
    }
}
